import { useNavigate } from "react-router-dom";
import { Select, MenuItem, Avatar } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StoreIcon from "@mui/icons-material/Store";
import BoltIcon from "@mui/icons-material/Bolt";
import SettingsIcon from "@mui/icons-material/Settings";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import AddCard from "../../Modals/addcard";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Modal,
  IconButton,
} from "@mui/material";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import { useState } from "react";

const revenueData = [
  { day: "Mon", revenue: 12000 },
  { day: "Tue", revenue: 15000 },
  { day: "Wed", revenue: 17000 },
  { day: "Thu", revenue: 14000 },
  { day: "Fri", revenue: 19000 },
  { day: "Sat", revenue: 22000 },
  { day: "Sun", revenue: 21000 },
];
const aiFeatures = [
  { name: "AI Business Generator", value: 342, total: 500, color: "#3b82f6" },
  { name: "AI Vendor Coach", value: 287, total: 500, color: "#22c55e" },
  { name: "AI Listing Assistant", value: 412, total: 500, color: "#f59e0b" },
  { name: "AI Support Copilot", value: 156, total: 500, color: "#06b6d4" },
];

const systemHealth = [
  {
    service: "Vendor Service",
    status: "operational",
    uptime: "99.9%",
    latency: "45ms",
    impact: "Low",
    actionNeeded: false,
  },
  {
    service: "Order Service",
    status: "degraded",
    uptime: "99.8%",
    latency: "52ms",
    impact: "Medium",
    actionNeeded: true,
  },
  {
    service: "AI Engine",
    status: "operational",
    uptime: "99.7%",
    latency: "120ms",
    impact: "High",
    actionNeeded: false,
  },
  {
    service: "Payment Gateway",
    status: "operational",
    uptime: "100%",
    latency: "38ms",
    impact: "Low",
    actionNeeded: false,
  },
];

const activities = [
  {
    title: "New vendor registered",
    subtitle: "Luxe Fashion Co.",
    time: "2 minutes ago",
    icon: <StoreIcon />,
    color: "#ff9800",
  },
  {
    title: "High-value order placed",
    subtitle: "Order #12847",
    time: "5 minutes ago",
    icon: <TrendingUpIcon />,
    color: "#4caf50",
  },
  {
    title: "AI model retrained",
    subtitle: "Recommendation Engine",
    time: "12 minutes ago",
    icon: <BoltIcon />,
    color: "#00bcd4",
  },
  {
    title: "Scheduled maintenance completed",
    subtitle: "Database Cluster",
    time: "1 hour ago",
    icon: <SettingsIcon />,
    color: "#8bc34a",
  },
  {
    title: "Vendor subscription upgraded",
    subtitle: "Urban Threads",
    time: "2 hours ago",
    icon: <UpgradeIcon />,
    color: "#2e7dff",
  },
];
const vendors = [
  {
    name: "Fashion House Dubai",
    email: "contact@fashionhouse.ae",
    status: "active",
    tier: "Enterprise",
    joined: "12/11/2025",
  },
  {
    name: "TechGadgets UAE",
    email: "info@techgadgets.ae",
    status: "active",
    tier: "Pro",
    joined: "12/11/2025",
  },
  {
    name: "Home Essentials",
    email: "sales@homeessentials.ae",
    status: "active",
    tier: "Pro",
    joined: "12/11/2025",
  },
  {
    name: "Beauty Palace",
    email: "hello@beautypalace.ae",
    status: "active",
    tier: "Enterprise",
    joined: "12/11/2025",
  },
  {
    name: "Sports Zone",
    email: "contact@sportszone.ae",
    status: "active",
    tier: "Basic",
    joined: "12/11/2025",
  },
  {
    name: "Luxury Watches ME",
    email: "info@luxurywatches.ae",
    status: "active",
    tier: "Enterprise",
    joined: "12/11/2025",
  },
  {
    name: "Kids World",
    email: "sales@kidsworld.ae",
    status: "active",
    tier: "Pro",
    joined: "12/11/2025",
  },
  {
    name: "Organic Foods",
    email: "orders@organicfoods.ae",
    status: "pending",
    tier: "Basic",
    joined: "12/11/2025",
  },
  {
    name: "Auto Parts Pro",
    email: "support@autopartspro.ae",
    status: "active",
    tier: "Pro",
    joined: "12/11/2025",
  },
  {
    name: "Pet Paradise",
    email: "info@petparadise.ae",
    status: "active",
    tier: "Basic",
    joined: "12/11/2025",
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState("super_admin");
  const [configMode, setConfigMode] = useState(false);
  const [breakdownModal, setBreakdownModal] = useState<{
    open: boolean;
    title: string;
    type: string;
  }>({
    open: false,
    title: "",
    type: "",
  });

  const [addCardModal, setAddCardModal] = useState(false);

  const handleOpenBreakdown = (title: string, type: string) => {
    setBreakdownModal({ open: true, title, type });
  };

  const attentionItems = [
    {
      label: "3 vendors pending KYC verification",
      status: "danger",
      icon: "🔴",
    },
    { label: "2 payout failures", status: "danger", icon: "🔴" },
    { label: "5 vendors near AI usage limit", status: "warning", icon: "🟠" },
    { label: "1 dispute escalated", status: "warning", icon: "🟠" },
    { label: "0 system incidents", status: "success", icon: "🟢" },
  ];

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        className="main_title"
      >
        <h1>Dashboard</h1>
        <Box display="flex" alignItems="center" gap={2} className="form">
       
          
          <Button
            variant={configMode ? "contained" : "outlined"}
            size="small"
            startIcon={
              configMode ? <CheckCircleOutlineIcon /> : <SettingsIcon />
            }
            onClick={() => setConfigMode(!configMode)}
          >
            {configMode ? "Done" : "Configure Cards"}
          </Button>
          {configMode && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddCardModal(true)}
            >
              Add Card
            </Button>
          )}
        </Box>
      </Box>
      {/* ===== Quick Actions ===== */}
      <div className="quick_actions cards">
        <div className="qa_header">
          <h3>Quick Actions</h3>
          <span className="muted">Frequently used operations</span>
        </div>

        <div className="qa_grid">
          <div
            className="qa_card blue"
            onClick={() => navigate("/manage-vendors/add-vendor")}
          >
            <h4>+ Add Vendor</h4>
            <p>Register a new vendor</p>
          </div>

        
          <div className="qa_card orange">
            <h4>Export Report</h4>
            <p>Download analytics</p>
          </div>

          <div className="qa_card red">
            <h4>View Incidents</h4>
            <p>Check active alerts</p>
          </div>

          <div
            className="qa_card cyan"
            onClick={() => navigate("/manage-customers")}
          >
            <h4>Manage Users</h4>
            <p>User administration</p>
          </div>

          <div className="qa_card teal">
            <h4>Notifications</h4>
            <p>View all alerts</p>
          </div>
        </div>
      </div>

      {/* ===== Admin Attention Panel ===== */}
      <div className="attention_panel cards mb_20">
        <div className="d_flex align-center gap_10 mb_15">
          <WarningAmberIcon color="error" />
          <h3 className="m_0">Requires Admin Attention (Today)</h3>
        </div>
        <div className="attention_grid">
          {attentionItems.map((item, idx) => (
            <div key={idx} className="attention_item">
              <span className="status_dot">{item.icon}</span>
              <Typography variant="body2">{item.label}</Typography>
            </div>
          ))}
        </div>
      </div>

      {/* ======= Stats Overview ======= */}
      <div className="stats_boxes gap_m">
        {/* Total Revenue */}
        {(selectedRole === "super_admin" ||
          selectedRole === "finance_admin") && (
            <div
              className="cards dashboard_stat_card"
              onClick={() => handleOpenBreakdown("Total Revenue", "revenue")}
            >
              {configMode && (
                <div className="card_config_overlay">
                  <IconButton   onClick={() => setAddCardModal(true)} size="small" className="config_btn">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" className="config_btn">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              <div className="data_top">
                <h2>
                  $847,250
                  <p>Total Revenue</p>
                </h2>
                <figure className="sucess">
                  <MonetizationOnOutlinedIcon fontSize="large" />
                </figure>
              </div>
              <div className="rating_box">
                <p className="green">+18.2% from last month</p>
              </div>
            </div>
          )}

        {/* AI Inquiries */}
        {(selectedRole === "super_admin" || selectedRole === "ops_admin") && (
          <div
            className="cards dashboard_stat_card"
            onClick={() => handleOpenBreakdown("AI Inquiries", "ai_inquiries")}
          >
            {configMode && (
              <div className="card_config_overlay">
                <IconButton size="small" className="config_btn">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="config_btn">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            )}
            <div className="data_top">
              <h2>
                12,847
                <p>AI Inquiries</p>
              </h2>
              <figure className="blue">
                <BoltOutlinedIcon fontSize="large" />
              </figure>
            </div>
            <div className="rating_box">
              <p className="green">+45% from yesterday</p>
            </div>
          </div>
        )}

        {/* AI Cost */}
        {(selectedRole === "super_admin" ||
          selectedRole === "finance_admin") && (
            <div
              className="cards dashboard_stat_card"
              onClick={() => handleOpenBreakdown("AI Cost", "ai_cost")}
            >
              {configMode && (
                <div className="card_config_overlay">
                  <IconButton   onClick={() => setAddCardModal(true)} size="small" className="config_btn">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" className="config_btn">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              <div className="data_top">
                <h2>
                  $2,145.80
                  <p>AI Cost</p>
                </h2>
                <figure className="warning">
                  <CreditCardOutlinedIcon fontSize="large" />
                </figure>
              </div>
              <div className="rating_box">
                <p className="red">-8% optimized this week</p>
              </div>
            </div>
          )}

        {/* Active Vendors */}
        {(selectedRole === "super_admin" || selectedRole === "ops_admin") && (
          <div
            className="cards dashboard_stat_card"
            onClick={() =>
              handleOpenBreakdown("Active Vendors", "active_vendors")
            }
          >
            {configMode && (
              <div className="card_config_overlay">
                <IconButton size="small" className="config_btn"   onClick={() => setAddCardModal(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="config_btn">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            )}
            <div className="data_top">
              <h2>
                9<p>Active Vendors</p>
              </h2>
              <figure className="blue">
                <StoreOutlinedIcon fontSize="large" />
              </figure>
            </div>
            <div className="rating_box">
              <p className="green">+12 new this week</p>
            </div>
          </div>
        )}

        {/* Pending Orders */}
        {(selectedRole === "super_admin" ||
          selectedRole === "ops_admin" ||
          selectedRole === "support_admin") && (
            <div
              className="cards dashboard_stat_card"
              onClick={() =>
                handleOpenBreakdown("Pending Orders", "pending_orders")
              }
            >
              {configMode && (
                <div className="card_config_overlay">
                  <IconButton   onClick={() => setAddCardModal(true)} size="small" className="config_btn">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" className="config_btn">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              <div className="data_top">
                <h2>
                  384
                  <p>Pending Orders</p>
                </h2>
                <figure className="warning">
                  <Inventory2OutlinedIcon fontSize="large" />
                </figure>
              </div>
              <div className="rating_box">
                <p className="orange">23 require attention</p>
              </div>
            </div>
          )}

        {/* Customer Satisfaction */}
        {(selectedRole === "super_admin" ||
          selectedRole === "support_admin") && (
            <div className="cards dashboard_stat_card">
              {configMode && (
                <div className="card_config_overlay">
                  <IconButton   onClick={() => setAddCardModal(true)} size="small" className="config_btn">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" className="config_btn">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              <div className="data_top">
                <h2>
                  4.8 / 5<p>Customer Satisfaction</p>
                </h2>
                <figure className="blue">
                  <StarBorderOutlinedIcon fontSize="large" />
                </figure>
              </div>
              <div className="rating_box">
                <p className="green">+0.3 from last quarter</p>
              </div>
            </div>
          )}

        {/* Conversion Rate */}
        {(selectedRole === "super_admin" || selectedRole === "ops_admin") && (
          <div className="cards dashboard_stat_card">
            {configMode && (
              <div className="card_config_overlay">
                <IconButton size="small" className="config_btn" >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" className="config_btn">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
            )}
            <div className="data_top">
              <h2>
                3.24%
                <p>Conversion Rate</p>
              </h2>
              <figure className="blue">
                <TrendingUpOutlinedIcon fontSize="large" />
              </figure>
            </div>
            <div className="rating_box">
              <p className="green">+0.5% improvement</p>
            </div>
          </div>
        )}

        {/* Monthly Growth */}
        {(selectedRole === "super_admin" ||
          selectedRole === "finance_admin") && (
            <div className="cards dashboard_stat_card">
              {configMode && (
                <div className="card_config_overlay">
                  <IconButton   onClick={() => setAddCardModal(true)} size="small" className="config_btn">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" className="config_btn">
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              )}
              <div className="data_top">
                <h2>
                  +24.5%
                  <p>Monthly Growth</p>
                </h2>
                <figure className="sucess">
                  <BarChartOutlinedIcon fontSize="large" />
                </figure>
              </div>
              <div className="rating_box">
                <p className="green">Above 20% target</p>
              </div>
            </div>
          )}
      </div>

      <div className=" mt_20">
        <Box display="grid" gridTemplateColumns="2fr 1fr" gap={2}>
          {/* REVENUE OVERVIEW */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography fontWeight={600}>Revenue Overview</Typography>
                <Select size="small" value="7">
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                </Select>
              </Box>

              <Box height={260}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData}>
                    <defs>
                      <linearGradient
                        id="revGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor="#2e7dff"
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="100%"
                          stopColor="#2e7dff"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" />
                    <YAxis />
                    <Tooltip />

                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#2e7dff"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      fill="url(#revGradient)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Total Revenue (7 days)
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  $114,000
                  <Chip
                    label="+23.5% vs last week"
                    size="small"
                    color="success"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* LIVE ACTIVITY */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography fontWeight={600}>Live Activity</Typography>
                <Chip label="Live" size="small" color="success" />
              </Box>

              <Stack spacing={2}>
                {activities.map((item, index) => (
                  <Box key={index} display="flex" gap={2}>
                    <Avatar sx={{ bgcolor: item.color }}>{item.icon}</Avatar>

                    <Box flex={1}>
                      <Typography fontWeight={600}>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.subtitle}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {item.time}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Box mt={2} textAlign="center">
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                >
                  View All Activity →
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </div>
      <div className=" mt_20">
        <Box display="grid" gridTemplateColumns="1.2fr 1fr" gap={2}>
          {/* AI FEATURES USAGE */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography fontWeight={600} mb={2}>
                AI Features Usage
              </Typography>

              <Stack spacing={2}>
                {aiFeatures.map((item, index) => {
                  const percent = (item.value / item.total) * 100;

                  return (
                    <Box key={index}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        mb={0.5}
                      >
                        <Typography variant="body2">{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.value} / {item.total}
                        </Typography>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        sx={{
                          height: 8,
                          borderRadius: 10,
                          backgroundColor: "#f1f5f9",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: item.color,
                            borderRadius: 10,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>

              <Button
                fullWidth
                sx={{
                  mt: 3,
                  background: "linear-gradient(135deg, #0f172a, #1e293b)",
                  color: "#fff",
                  fontWeight: 600,
                  py: 1,
                  borderRadius: 2,
                  "&:hover": {
                    background: "linear-gradient(135deg, #020617, #0f172a)",
                  },
                }}
              >
                Manage AI Features
              </Button>
            </CardContent>
          </Card>

          {/* SYSTEM HEALTH */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography fontWeight={600}>System Health</Typography>
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                >
                  View All
                </Typography>
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Impact</TableCell>
                    <TableCell>Action Required</TableCell>
                    <TableCell>Uptime</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {systemHealth.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.service}</TableCell>

                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          sx={{
                            backgroundColor:
                              row.status === "operational"
                                ? "#dcfce7"
                                : "#fee2e2",
                            color:
                              row.status === "operational"
                                ? "#166534"
                                : "#ef4444",
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <span
                          className={`impact_badge ${row.impact.toLowerCase()}`}
                        >
                          {row.impact}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span
                          className={`health_status_label ${row.actionNeeded ? "action_needed" : "no_action"}`}
                        >
                          {row.actionNeeded
                            ? "Admin attention needed"
                            : "No action required"}
                        </span>
                      </TableCell>

                      <TableCell>{row.uptime}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                Showing 4 of 4 results
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </div>
      <div className="mt_20">
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography fontWeight={600}>Recent Vendors</Typography>

              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: "pointer", fontWeight: 600 }}
              >
                View All Vendors
              </Typography>
            </Box>
            <div className="table_container">
              {/* Table */}
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>BUSINESS NAME</TableCell>
                    <TableCell>EMAIL</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell>TIER</TableCell>
                    <TableCell>JOINED</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {vendors.map((vendor, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight={500} sx={{cursor:"pointer"}} onClick={() => navigate("/manage-vendors/view-vendor")}>{vendor.name}</Typography>
                      </TableCell>

                      <TableCell sx={{ color: "text.secondary" }}>
                        {vendor.email}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={vendor.status}
                          size="small"
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: 600,
                            backgroundColor:
                              vendor.status === "active"
                                ? "#dcfce7"
                                : "#fef3c7",
                            color:
                              vendor.status === "active"
                                ? "#166534"
                                : "#92400e",
                          }}
                        />
                      </TableCell>

                      <TableCell>{vendor.tier}</TableCell>

                      <TableCell>{vendor.joined}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Footer */}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={2}
            >
              Showing 10 of 10 results
            </Typography>
          </CardContent>
        </Card>
      </div>
      <Modal
        open={breakdownModal.open}
        onClose={() => setBreakdownModal({ ...breakdownModal, open: false })}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="breakdown_modal_box">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography id="modal-modal-title" variant="h6" component="h2">
              {breakdownModal.title} Breakdown
            </Typography>
            <IconButton
              onClick={() =>
                setBreakdownModal({ ...breakdownModal, open: false })
              }
            >
              <AddIcon sx={{ transform: "rotate(45deg)" }} />
            </IconButton>
          </Box>

          <div className="modal_content_body">
            {breakdownModal.type === "pending_orders" && (
              <Stack spacing={2}>
                <div className="info_card danger">
                  <h5>🔴 Orders stuck {">"} 24h: 42</h5>
                  <p>Critical attention needed for vendor fulfillment.</p>
                </div>
                <div className="info_card warning">
                  <h5>🟠 Top vendors causing delays:</h5>
                  <ul>
                    <li>TechHub Electronics (12 orders)</li>
                    <li>Fashion Hub (8 orders)</li>
                  </ul>
                </div>
              </Stack>
            )}
            {breakdownModal.type === "ai_cost" && (
              <Stack spacing={2}>
                <div className="info_card blue">
                  <h5>📊 Top AI-consuming Vendors:</h5>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor</TableCell>
                        <TableCell align="right">Cost ($)</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Luxe Fashion</TableCell>
                        <TableCell align="right">$450</TableCell>
                        <TableCell align="right">1.2M</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Gadget Shop</TableCell>
                        <TableCell align="right">$320</TableCell>
                        <TableCell align="right">0.8M</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <div className="info_card warning">
                  <h5>⚠️ Abnormal Usage Patterns Detected:</h5>
                  <p>
                    Sudden spike in AI Inquiries from "Beauty Palace" (300%
                    above baseline).
                  </p>
                </div>
              </Stack>
            )}
            {breakdownModal.type !== "pending_orders" &&
              breakdownModal.type !== "ai_cost" && (
                <Typography variant="body1">
                  Detailed breakdown data for {breakdownModal.title} will be
                  loaded here from the analytics service.
                </Typography>
              )}
          </div>
        </Box>
      </Modal>
      <AddCard
        open={addCardModal}
        onClose={() => setAddCardModal(false)}
        setOpen={setAddCardModal}
      />
    </>
  );
};

export default Dashboard;
